import { LogOutIcon, UserIcon } from "lucide-react";
import { Link, useNavigate } from "react-router";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/hooks/use-auth";

/** Who's signed in, and the way out. Sits beside the theme toggle. */
export function AuthMenu() {
  const { session, signOut } = useAuth();
  const navigate = useNavigate();

  // Still reading the stored session — a placeholder rather than a "Sign in"
  // button that would flip to a menu a moment later.
  if (session === undefined) return <Skeleton className="size-9 rounded-md" />;

  if (session === null) {
    return (
      <Button asChild variant="ghost" size="sm">
        <Link to="/sign-in">Sign in</Link>
      </Button>
    );
  }

  async function handleSignOut() {
    try {
      await signOut();
      void navigate("/", { replace: true });
    } catch (error) {
      toast.error("Couldn't sign you out", {
        description: error instanceof Error ? error.message : undefined,
      });
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Account">
          <UserIcon />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel className="max-w-56 truncate font-normal text-muted-foreground">
          {session.user.email}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => void handleSignOut()}>
          <LogOutIcon className="size-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
